import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  Users, 
  ShoppingCart, 
  DollarSign,
  AlertTriangle,
  Calendar,
  Activity,
  RefreshCcw,
  BarChart3,
  TrendingUpIcon,
  Package,
  User
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { dashboardService, DashboardData } from '../services/dashboardService';
import SalesChart from '../components/SalesChart';
import { customToast } from '../utils/customToast';
import { useTimezone } from '../utils/timezone';
import { useTitle } from '../hooks/useTitle';
import { useCurrency } from '../hooks/useCurrency';

const DashboardPage: React.FC = () => {
  const { theme } = useTheme();
  const { user, isLoading: isAuthLoading } = useAuth();
  const { formatCurrency } = useCurrency();
  const { formatTime } = useTimezone();
  const navigate = useNavigate();
  
  // Set page title
  useTitle('Dashboard');
  
  // State management
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'week' | 'month'>('today');
  const [chartPeriod, setChartPeriod] = useState<'7days' | '30days'>('7days');
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingChart, setLoadingChart] = useState(false);

  // Load dashboard data
  useEffect(() => {
    // Don't load data if auth is still loading or user is not authenticated
    if (isAuthLoading || !user) return;
    
    loadDashboardData();
  }, [selectedPeriod, isAuthLoading, user]);

  // Separate effect for chart data
  useEffect(() => {
    if (isAuthLoading || !user || !dashboardData) return;
    
    loadChartData();
  }, [chartPeriod, isAuthLoading, user]);

  const loadDashboardData = async () => {
    try {
      setLoadingStats(true);
      const data = await dashboardService.getDashboardData(selectedPeriod);
      
      // Load chart data based on current chart period
      const salesChart = await dashboardService.getSalesChart(chartPeriod);
      data.salesChart = salesChart;
      
      console.log('Dashboard data received:', data);
      console.log('Stats from data:', data.stats);
      console.log('Chart period:', chartPeriod, 'Chart data points:', salesChart.length);
      setDashboardData(data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      customToast.error('Failed to load dashboard data');
    } finally {
      setLoadingStats(false);
      setLoading(false);
    }
  };

  const loadChartData = async () => {
    try {
      setLoadingChart(true);
      const salesChart = await dashboardService.getSalesChart(chartPeriod);
      setDashboardData(prev => prev ? { ...prev, salesChart } : null);
    } catch (error) {
      console.error('Failed to load chart data:', error);
      customToast.error('Failed to load chart data');
    } finally {
      setLoadingChart(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const data = await dashboardService.getDashboardData(selectedPeriod);
      setDashboardData(data);
      customToast.success('Dashboard refreshed successfully');
    } catch (error) {
      console.error('Failed to refresh dashboard:', error);
      customToast.error('Failed to refresh dashboard');
    } finally {
      setRefreshing(false);
    }
  };

  const handleChartPeriodChange = (period: '7days' | '30days') => {
    setChartPeriod(period);
    // Data will be reloaded automatically due to useEffect dependency
  };

  if (loading && !dashboardData) {
    return (
      <div className="space-y-3 sm:space-y-4 lg:space-y-6 p-2 sm:p-4 lg:p-0">
        {/* Header - VISIBLE (not skeleton) */}
        <div className="space-y-3 sm:space-y-4">
          <div>
            <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Dashboard
            </h1>
            <p className={`mt-1 text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Welcome back! Here's what's happening at your store today.
            </p>
          </div>
          
          {/* Controls - VISIBLE (period buttons, refresh button, last updated) */}
          <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            {/* Period selector - VISIBLE */}
            <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-4">
              <div className="grid grid-cols-3 gap-1.5 sm:flex sm:space-x-2 sm:gap-0">
                {['today', 'week', 'month'].map((period) => (
                  <button
                    key={period}
                    disabled={true}
                    className={`px-2.5 py-2 sm:py-1.5 text-sm font-medium rounded-lg transition-colors touch-manipulation opacity-50 ${
                      theme === 'dark'
                        ? 'text-gray-400 bg-slate-700'
                        : 'text-gray-600 bg-gray-100'
                    }`}
                  >
                    {period.charAt(0).toUpperCase() + period.slice(1)}
                  </button>
                ))}
              </div>
              
              {/* Refresh button - VISIBLE but disabled */}
              <button
                disabled={true}
                className={`flex items-center justify-center space-x-2 px-3 sm:px-4 py-2.5 sm:py-1.5 rounded-lg border opacity-50 min-h-[44px] sm:min-h-0 ${
                  theme === 'dark'
                    ? 'border-slate-600 text-gray-400 bg-slate-700'
                    : 'border-gray-300 text-gray-600 bg-gray-100'
                }`}
              >
                <RefreshCcw size={16} />
                <span className="text-sm font-medium">Refresh</span>
              </button>
            </div>
            
            {/* Last updated - VISIBLE */}
            <div className={`hidden sm:flex items-center space-x-2 text-sm ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              <Activity size={16} />
              <span>Last updated: {formatTime(new Date())}</span>
            </div>
          </div>
        </div>

        {/* Stats Cards - Labels VISIBLE, Only Values are Skeleton */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
          {[
            { icon: DollarSign, title: 'Total Sales', color: 'blue' },
            { icon: ShoppingCart, title: 'Transactions', color: 'green' },
            { icon: Users, title: 'Total Customers', color: 'purple' },
            { icon: Package, title: 'Products Stock', color: 'orange' },
            { icon: Calendar, title: 'Pending Dues', color: 'red' },
            { icon: TrendingUp, title: 'Profit', color: 'green' }
          ].map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className={`p-3 sm:p-4 lg:p-6 rounded-2xl border transition-all ${
                  theme === 'dark'
                    ? 'bg-slate-800 border-slate-700 hover:border-slate-600'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    {/* Label - VISIBLE */}
                    <p className={`text-xs sm:text-sm font-medium ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {stat.title}
                    </p>
                    
                    {/* Value - SKELETON */}
                    <div className={`mt-2 sm:mt-3 h-8 sm:h-9 w-32 rounded-lg animate-pulse ${
                      theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                    }`} />
                    
                    {/* Change info - SKELETON */}
                    <div className={`mt-2 h-3 w-20 rounded-lg animate-pulse ${
                      theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                    }`} />
                  </div>
                  
                  {/* Icon - VISIBLE */}
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ml-3 ${
                    stat.color === 'blue' ? (theme === 'dark' ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600') :
                    stat.color === 'green' ? (theme === 'dark' ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-600') :
                    stat.color === 'purple' ? (theme === 'dark' ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-600') :
                    stat.color === 'indigo' ? (theme === 'dark' ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-600') :
                    stat.color === 'orange' ? (theme === 'dark' ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-600') :
                    (theme === 'dark' ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-600')
                  }`}>
                    <Icon size={20} className="sm:w-6 sm:h-6" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Charts and Analytics - Labels VISIBLE, Data SKELETON */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 xl:items-stretch">
          {/* Sales Chart - Title and Controls VISIBLE, Chart Data SKELETON */}
          <div className={`xl:col-span-2 order-2 xl:order-1 p-3 sm:p-4 lg:p-6 rounded-2xl border ${
            theme === 'dark'
              ? 'bg-slate-800 border-slate-700'
              : 'bg-white border-gray-200'
          }`}>
            {/* Title and Period Controls - VISIBLE */}
            <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 mb-4 sm:mb-6">
              <h3 className={`text-base sm:text-lg font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Sales Chart
              </h3>
              {/* Legend - VISIBLE */}
              <div className="hidden sm:flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${
                    theme === 'dark' ? 'bg-blue-500' : 'bg-blue-400'
                  }`} />
                  <span className={`text-xs ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>Sales</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${
                    theme === 'dark' ? 'bg-green-500' : 'bg-green-400'
                  }`} />
                  <span className={`text-xs ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>Profit</span>
                </div>
              </div>
            </div>

            {/* Chart Area - SKELETON */}

            {/* Chart bars area - Visible axis/structure, only bar heights are skeleton animated */}
            <div className={`flex-1 rounded-lg mb-4 sm:mb-6 flex items-end justify-around px-4 py-6 sm:py-8 gap-2 sm:gap-3 ${
              theme === 'dark' ? 'bg-slate-700/30' : 'bg-gray-50'
            }`} style={{ minHeight: '250px' }}>
              {[...Array(7)].map((_, idx) => (
                <div key={idx} className="flex-1 flex flex-col items-center space-y-2 h-full">
                  {/* Bar group with sales and profit bars - SKELETON */}
                  <div className="flex items-end justify-center space-x-1 h-full mb-2">
                    <div 
                      className={`rounded-t-sm animate-pulse ${
                        theme === 'dark' ? 'bg-blue-500/60' : 'bg-blue-400'
                      }`} 
                      style={{ width: '14px', height: `${Math.random() * 120 + 40}px` }} 
                    />
                    <div 
                      className={`rounded-t-sm animate-pulse ${
                        theme === 'dark' ? 'bg-green-500/60' : 'bg-green-400'
                      }`} 
                      style={{ width: '14px', height: `${Math.random() * 100 + 20}px` }} 
                    />
                  </div>
                  {/* X-axis label - SKELETON */}
                  <div className={`h-3 w-8 rounded-lg animate-pulse ${
                    theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                  }`} />
                </div>
              ))}
            </div>

            {/* Summary section - Labels VISIBLE, Values SKELETON */}
            <div className={`pt-3 sm:pt-4 border-t flex flex-col space-y-3 sm:flex-row sm:justify-between sm:space-y-0 ${
              theme === 'dark' ? 'border-slate-700' : 'border-gray-200'
            }`}>
              <div className="space-y-2">
                <p className={`text-xs font-medium ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>Total Sales</p>
                <div className={`h-6 w-32 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                }`} />
              </div>
              <div className="space-y-2">
                <p className={`text-xs font-medium ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>Total Profit</p>
                <div className={`h-6 w-32 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                }`} />
              </div>
            </div>
          </div>

          {/* Top Selling Products - Title and Structure VISIBLE, Data SKELETON */}
          <div className={`p-3 sm:p-4 lg:p-6 rounded-2xl border order-1 xl:order-2 ${
            theme === 'dark'
              ? 'bg-slate-800 border-slate-700'
              : 'bg-white border-gray-200'
          }`}>
            {/* Title - VISIBLE */}
            <h3 className={`text-base sm:text-lg font-semibold mb-4 sm:mb-6 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Top Selling Products
            </h3>
            
            <div className="space-y-3 sm:space-y-4 flex-1">
              {[...Array(3)].map((_, idx) => (
                <div key={idx}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      {/* Rank badge - Visible colored icon */}
                      <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center font-semibold text-xs sm:text-sm ${
                        idx === 0 ? (theme === 'dark' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600') :
                        idx === 1 ? (theme === 'dark' ? 'bg-gray-400/20 text-gray-300' : 'bg-gray-200 text-gray-600') :
                        (theme === 'dark' ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-600')
                      }`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        {/* Product name - SKELETON */}
                        <div className={`h-4 w-28 rounded-lg animate-pulse ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        {/* Units sold info - SKELETON */}
                        <div className={`mt-1.5 h-3 w-20 rounded-lg animate-pulse ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                    </div>
                    {/* Revenue amount - SKELETON */}
                    <div className={`h-4 w-20 rounded-lg animate-pulse flex-shrink-0 ${
                      theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                    }`} />
                  </div>
                  {idx < 2 && <div className={`border-t mt-3 ${
                    theme === 'dark' ? 'border-slate-700' : 'border-gray-200'
                  }`} />}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Activity & Alerts - Titles and Structure VISIBLE, Data SKELETON */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
          {/* Recent Sales - Title and Structure VISIBLE, Data SKELETON */}
          <div className={`p-3 sm:p-4 lg:p-6 rounded-2xl border ${
            theme === 'dark'
              ? 'bg-slate-800 border-slate-700'
              : 'bg-white border-gray-200'
          }`}>
            {/* Title and View All button - VISIBLE */}
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className={`text-base sm:text-lg font-semibold ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Recent Sales
              </h3>
              <button className={`text-xs sm:text-sm font-medium px-2 sm:px-3 py-1 sm:py-2 rounded-lg transition-colors opacity-50 cursor-not-allowed ${
                theme === 'dark' 
                  ? 'bg-slate-700 text-gray-300' 
                  : 'bg-gray-200 text-gray-500'
              }`}>
                View All
              </button>
            </div>
            
            <div className="space-y-2.5 sm:space-y-3 lg:space-y-4">
              {[...Array(4)].map((_, idx) => (
                <div key={idx}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                      {/* Avatar circle - Visible placeholder icon */}
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        theme === 'dark' 
                          ? 'bg-blue-500/20 text-blue-400' 
                          : 'bg-blue-100 text-blue-600'
                      }`}>
                        <User size={16} className="sm:w-5 sm:h-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        {/* Customer name - SKELETON */}
                        <div className={`h-4 w-32 rounded-lg animate-pulse ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        {/* Date and status */}
                        <div className="flex items-center space-x-2 mt-1.5">
                          {/* Date - SKELETON */}
                          <div className={`h-3 w-20 rounded-lg animate-pulse ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                          {/* Status badge - SKELETON */}
                          <div className={`h-4 w-14 rounded-full animate-pulse ${
                            theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                          }`} />
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2 space-y-1.5">
                      {/* Amount - SKELETON */}
                      <div className={`h-5 w-20 rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                      }`} />
                      {/* Item count - SKELETON */}
                      <div className={`h-3 w-16 rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                      }`} />
                    </div>
                  </div>
                  {idx < 3 && <div className={`border-t mt-2.5 sm:mt-3 lg:mt-4 ${
                    theme === 'dark' ? 'border-slate-700' : 'border-gray-200'
                  }`} />}
                </div>
              ))}
            </div>
          </div>

          {/* Low Stock Alerts - Title and Structure VISIBLE, Data SKELETON */}
          <div className={`p-3 sm:p-4 lg:p-6 rounded-2xl border ${
            theme === 'dark'
              ? 'bg-slate-800 border-slate-700'
              : 'bg-white border-gray-200'
          }`}>
            {/* Title - VISIBLE */}
            <h3 className={`text-base sm:text-lg font-semibold mb-4 sm:mb-6 flex items-center space-x-2 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              <AlertTriangle size={18} className="sm:w-5 sm:h-5 text-orange-500" />
              <span>Low Stock Alerts</span>
            </h3>
            
            <div className="space-y-2.5 sm:space-y-3 lg:space-y-4">
              {[...Array(4)].map((_, idx) => (
                <div key={idx}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      {/* Status indicator dot - VISIBLE */}
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        theme === 'dark' ? 'bg-orange-500' : 'bg-orange-400'
                      }`} />
                      <div className="min-w-0 flex-1">
                        {/* Product name - SKELETON */}
                        <div className={`h-4 w-36 rounded-lg animate-pulse ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                        {/* SKU and stock - SKELETON */}
                        <div className={`mt-1.5 h-3 w-28 rounded-lg animate-pulse ${
                          theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                        }`} />
                      </div>
                    </div>
                    {/* Current stock quantity - SKELETON */}
                    <div className={`h-5 w-12 rounded-lg animate-pulse flex-shrink-0 ml-2 ${
                      theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300'
                    }`} />
                  </div>
                  {idx < 3 && <div className={`border-t mt-2.5 sm:mt-3 lg:mt-4 ${
                    theme === 'dark' ? 'border-slate-700' : 'border-gray-200'
                  }`} />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="text-center py-12">
        <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
          Failed to load dashboard data
        </p>
      </div>
    );
  }

  const { stats } = dashboardData;

  // Helper function to get the appropriate label based on selected period
  const getSalesLabel = () => {
    switch (selectedPeriod) {
      case 'today':
        return 'Today\'s Sales';
      case 'week':
        return 'Weekly Sales';
      case 'month':
        return 'Monthly Sales';
      default:
        return 'Total Sales';
    }
  };

  // Helper function to get the appropriate sales value based on selected period
  const getSalesValue = () => {
    switch (selectedPeriod) {
      case 'today':
        return stats.todaySales;
      case 'week':
        return stats.totalSales; // This should be weekly sales from backend
      case 'month':
        return stats.totalSales; // This should be monthly sales from backend
      default:
        return stats.totalSales;
    }
  };

  const statsCards = [
    {
      title: getSalesLabel(),
      value: formatCurrency(getSalesValue()),
      change: `${(stats.monthlyGrowth || 0) >= 0 ? '+' : ''}${(stats.monthlyGrowth || 0).toFixed(1)}%`,
      changeType: (stats.monthlyGrowth || 0) >= 0 ? 'positive' as const : 'negative' as const,
      icon: DollarSign,
      color: 'blue'
    },
    {
      title: 'Transactions',
      value: `${selectedPeriod === 'today' ? stats.todaysTransactions : stats.periodTransactions}`,
      change: `${selectedPeriod === 'today' ? 'Today' : selectedPeriod === 'week' ? 'This week' : 'This month'}`,
      changeType: 'positive' as const,
      icon: ShoppingCart,
      color: 'green'
    },
    {
      title: 'Total Customers',
      value: stats.totalCustomers.toLocaleString(),
      change: `${(stats.weeklyGrowth || 0) >= 0 ? '+' : ''}${(stats.weeklyGrowth || 0).toFixed(1)}%`,
      changeType: (stats.weeklyGrowth || 0) >= 0 ? 'positive' as const : 'negative' as const,
      icon: Users,
      color: 'purple'
    },
    {
      title: 'Products Stock',
      value: stats.totalProducts.toLocaleString(),
      change: `${stats.lowStockItems || 0} low stock`,
      changeType: (stats.lowStockItems || 0) > 0 ? 'warning' as const : 'positive' as const,
      icon: ShoppingCart,
      color: 'orange'
    },
    {
      title: 'Pending Dues',
      value: formatCurrency(stats.pendingDues),
      change: 'Collect pending',
      changeType: 'warning' as const,
      icon: Calendar,
      color: 'red'
    },
    {
      title: `${selectedPeriod === 'today' ? 'Today\'s' : selectedPeriod === 'week' ? 'Weekly' : 'Monthly'} Profit`,
      value: formatCurrency(stats.monthlyProfit || 0),
      change: `${(stats.profitGrowth || 0) >= 0 ? '+' : ''}${(stats.profitGrowth || 0).toFixed(1)}%`,
      changeType: (stats.profitGrowth || 0) >= 0 ? 'positive' as const : 'negative' as const,
      icon: TrendingUpIcon,
      color: 'green'
    },
  ];

  const getColorClasses = (color: string) => {
    const colors = {
      blue: theme === 'dark' ? 'bg-blue-900/20 text-blue-400' : 'bg-blue-100 text-blue-600',
      green: theme === 'dark' ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600',
      purple: theme === 'dark' ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-600',
      orange: theme === 'dark' ? 'bg-orange-900/20 text-orange-400' : 'bg-orange-100 text-orange-600',
      red: theme === 'dark' ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-600',
      yellow: theme === 'dark' ? 'bg-yellow-900/20 text-yellow-400' : 'bg-yellow-100 text-yellow-600',
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  const getChangeClasses = (changeType: 'positive' | 'negative' | 'warning') => {
    if (changeType === 'positive') {
      return theme === 'dark' ? 'text-green-400' : 'text-green-600';
    } else if (changeType === 'negative') {
      return theme === 'dark' ? 'text-red-400' : 'text-red-600';
    } else {
      return theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600';
    }
  };

  return (
    <div className="space-y-3 sm:space-y-4 lg:space-y-6 p-2 sm:p-4 lg:p-0">
      {/* Subscription Warning Banner */}
      {dashboardData.subscription && (
        <>
          {dashboardData.subscription.subscription_status === 'suspended' && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-600 p-4 rounded-lg">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-red-800 dark:text-red-300">
                    Account Suspended
                  </h3>
                  <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                    Your shop account has been suspended. Please contact support to reactivate your account.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {dashboardData.subscription.subscription_status === 'expired' && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-600 p-4 rounded-lg">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-red-800 dark:text-red-300">
                    Subscription Expired
                  </h3>
                  <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                    Your subscription has expired. Please renew to continue using all features.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {dashboardData.subscription.days_remaining !== null && 
           dashboardData.subscription.days_remaining <= 7 && 
           dashboardData.subscription.days_remaining > 0 &&
           dashboardData.subscription.subscription_status === 'active' && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-600 p-4 rounded-lg">
              <div className="flex items-start">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-yellow-800 dark:text-yellow-300">
                    Subscription Expiring Soon
                  </h3>
                  <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                    Your subscription will expire in {dashboardData.subscription.days_remaining} days. 
                    Please contact support to renew.
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Header */}
      <div className="space-y-3 sm:space-y-4">
        <div>
          <h1 className={`text-xl sm:text-2xl lg:text-3xl font-bold ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Dashboard
          </h1>
          <p className={`mt-1 text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Welcome back! Here's what's happening at your store today.
          </p>
        </div>
        
        {/* Mobile-first controls layout */}
        <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          {/* Period selector - full width on mobile */}
          <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-4">
            <div className="grid grid-cols-3 gap-1.5 sm:flex sm:space-x-2 sm:gap-0">
              {['today', 'week', 'month'].map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period as 'today' | 'week' | 'month')}
                  className={`px-2.5 py-2 sm:py-1.5 text-sm font-medium rounded-lg transition-colors touch-manipulation ${
                    selectedPeriod === period
                      ? 'bg-blue-600 text-white shadow-md'
                      : theme === 'dark'
                        ? 'text-gray-400 hover:text-white hover:bg-slate-700 active:bg-slate-600'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200'
                  }`}
                >
                  {period.charAt(0).toUpperCase() + period.slice(1)}
                </button>
              ))}
            </div>

            {/* Refresh button */}
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className={`flex items-center justify-center space-x-2 px-3 sm:px-4 py-2.5 sm:py-1.5 rounded-lg border transition-all touch-manipulation min-h-[44px] sm:min-h-0 ${
                refreshing
                  ? 'opacity-50 cursor-not-allowed'
                  : theme === 'dark'
                    ? 'border-slate-600 text-gray-300 hover:border-slate-500 hover:bg-slate-700 active:bg-slate-600'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50 active:bg-gray-100'
              }`}
              title="Refresh Dashboard"
            >
              <RefreshCcw size={16} className={refreshing ? 'animate-spin' : ''} />
              <span className="text-sm font-medium">Refresh</span>
            </button>
          </div>

          {/* Last updated - hidden on small mobile, visible on larger screens */}
          <div className={`hidden sm:flex items-center space-x-2 text-sm ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            <Activity size={16} />
            <span>Last updated: {formatTime(new Date())}</span>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        {statsCards.map((stat, index) => {
          const Icon = stat.icon;
          const isLoading = loadingStats;
          
          return (
            <div
              key={index}
              className={`p-3 sm:p-4 lg:p-6 rounded-2xl border transition-all duration-200 hover:shadow-lg touch-manipulation ${
                theme === 'dark'
                  ? 'bg-slate-800 border-slate-700 hover:border-slate-600'
                  : 'bg-white border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className={`text-xs sm:text-sm font-medium ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    {stat.title}
                  </p>
                  
                  {isLoading ? (
                    <>
                      {/* Value Skeleton */}
                      <div className={`mt-1 sm:mt-2 h-8 sm:h-10 w-32 rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                      }`} />
                      
                      {/* Change Skeleton */}
                      <div className={`mt-1 sm:mt-2 h-4 w-20 rounded-lg animate-pulse ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                      }`} />
                    </>
                  ) : (
                    <>
                      <p className={`mt-1 sm:mt-2 text-xl sm:text-2xl font-bold truncate ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>
                        {stat.value}
                      </p>
                      <div className={`mt-1 sm:mt-2 flex items-center text-xs sm:text-sm ${getChangeClasses(stat.changeType)}`}>
                        {stat.changeType === 'positive' && <TrendingUp size={14} className="mr-1 flex-shrink-0" />}
                        {stat.changeType === 'warning' && <AlertTriangle size={14} className="mr-1 flex-shrink-0" />}
                        <span className="truncate">{stat.change}</span>
                      </div>
                    </>
                  )}
                </div>
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 ml-3 ${
                  getColorClasses(stat.color)
                }`}>
                  <Icon size={20} className="sm:w-6 sm:h-6" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Charts and Analytics */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 sm:gap-4 lg:gap-6 xl:items-stretch">
        {/* Sales Chart */}
        <div className="xl:col-span-2 order-2 xl:order-1 min-h-[500px] flex flex-col">
          {!dashboardData?.salesChart || dashboardData.salesChart.length === 0 ? (
            // Initial load skeleton - complete skeleton
            <div className={`p-3 sm:p-4 lg:p-6 rounded-2xl border flex flex-col h-full ${
              theme === 'dark'
                ? 'bg-slate-800 border-slate-700'
                : 'bg-white border-gray-200'
            }`}>
              {/* Title skeleton */}
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div className={`h-6 w-32 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                }`} />
              </div>
              
              {/* Chart area skeleton */}
              <div className={`flex-1 rounded-lg animate-pulse mb-4 sm:mb-6 ${
                theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
              }`} style={{ minHeight: '250px' }} />
              
              {/* Summary section skeleton - separate from chart */}
              <div className={`pt-3 sm:pt-4 border-t ${
                theme === 'dark' ? 'border-slate-700' : 'border-gray-200'
              }`}>
                <div className="flex flex-col space-y-3 sm:flex-row sm:justify-between sm:space-y-0">
                  <div className="space-y-2">
                    <div className={`h-4 w-20 rounded-lg animate-pulse ${
                      theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                    }`} />
                    <div className={`h-6 w-32 rounded-lg animate-pulse ${
                      theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                    }`} />
                  </div>
                  <div className="space-y-2">
                    <div className={`h-4 w-24 rounded-lg animate-pulse ${
                      theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                    }`} />
                    <div className={`h-6 w-32 rounded-lg animate-pulse ${
                      theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                    }`} />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="xl:col-span-2 order-2 xl:order-1 relative">
              <SalesChart 
                data={dashboardData?.salesChart || []} 
                title="Sales Overview"
                showProfit={true}
                loading={loadingChart}
              >
                {/* Chart period selector - moved inside the chart card */}
                <div className="grid grid-cols-2 gap-1.5 sm:flex sm:justify-center sm:space-x-2 sm:gap-0">
                  {[
                    { key: '7days' as const, label: '7 Days' },
                    { key: '30days' as const, label: '30 Days' }
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => handleChartPeriodChange(key)}
                      disabled={loadingChart}
                      className={`px-2.5 py-2 sm:py-1.5 text-sm font-medium rounded-lg transition-colors touch-manipulation ${
                        chartPeriod === key
                          ? 'bg-blue-600 text-white shadow-md'
                          : theme === 'dark'
                            ? 'text-gray-400 hover:text-white hover:bg-slate-700 active:bg-slate-600'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200'
                      } ${loadingChart ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </SalesChart>
            </div>
          )}
        </div>

        {/* Top Selling Products */}
        <div className={`p-3 sm:p-4 lg:p-6 rounded-2xl border order-1 xl:order-2 flex flex-col min-h-[500px] ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between mb-4 sm:mb-6">
            <h3 className={`text-base sm:text-lg font-semibold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Top Selling ({selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)})
            </h3>
            <BarChart3 className={`w-4 h-4 sm:w-5 sm:h-5 ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`} />
          </div>
          
          <div className="space-y-3 sm:space-y-4 flex-1 overflow-y-auto">
            {loadingStats ? (
              <>
                {[...Array(3)].map((_, idx) => (
                  <div key={idx}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                          idx === 0 
                            ? 'bg-yellow-500 text-white'
                            : idx === 1 
                              ? 'bg-gray-400 text-white'
                              : idx === 2
                                ? 'bg-amber-600 text-white'
                                : theme === 'dark' 
                                  ? 'bg-slate-700 text-gray-300' 
                                  : 'bg-gray-100 text-gray-700'
                        }`}>
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`h-4 w-24 rounded-lg animate-pulse ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                          }`} />
                          <div className={`mt-1.5 h-3 w-16 rounded-lg animate-pulse ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                          }`} />
                        </div>
                      </div>
                      <div className={`h-4 w-16 rounded-lg animate-pulse flex-shrink-0 ml-2 ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-200'
                      }`} />
                    </div>
                    {idx < 2 && <div className={`border-t mt-3 ${
                      theme === 'dark' ? 'border-slate-700' : 'border-gray-200'
                    }`} />}
                  </div>
                ))}
              </>
            ) : dashboardData?.topSelling.length === 0 ? (
              <div className={`text-center py-8 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <TrendingUp className={`w-12 h-12 mx-auto mb-3 ${
                  theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
                }`} />
                <p className="text-sm font-medium">No sales data yet</p>
                <p className="text-xs mt-1">Top selling products will appear here</p>
              </div>
            ) : (
              dashboardData?.topSelling.slice(0, 5).map((product, index) => (
                <React.Fragment key={`top-selling-${index}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                      <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                        index === 0 
                          ? 'bg-yellow-500 text-white'
                          : index === 1 
                            ? 'bg-gray-400 text-white'
                            : index === 2
                              ? 'bg-amber-600 text-white'
                              : theme === 'dark' 
                                ? 'bg-slate-700 text-gray-300' 
                                : 'bg-gray-100 text-gray-700'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`font-medium text-sm truncate ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {product.product_name}
                        </p>
                        <p className={`text-xs ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {product.quantity_sold} units sold
                        </p>
                      </div>
                    </div>
                    <div className={`text-sm font-semibold flex-shrink-0 ml-2 ${
                      theme === 'dark' ? 'text-green-400' : 'text-green-600'
                    }`}>
                      {formatCurrency(product.revenue)}
                    </div>
                  </div>
                  {index < (dashboardData?.topSelling.length || 0) - 1 && (
                    <div className={`border-t ${
                      theme === 'dark' ? 'border-slate-700' : 'border-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 lg:gap-6">
        {/* Recent Sales */}
        <div className={`p-3 sm:p-4 lg:p-6 rounded-2xl border ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between mb-3 sm:mb-4 lg:mb-6">
            <h2 className={`text-lg sm:text-xl font-semibold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Recent Sales
            </h2>
            <button 
              onClick={() => navigate('/dashboard/sales')}
              className={`text-sm font-medium transition-colors px-3 py-1.5 rounded-lg touch-manipulation ${
                theme === 'dark'
                  ? 'text-blue-400 hover:text-blue-300 hover:bg-slate-700 active:bg-slate-600'
                  : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50 active:bg-blue-100'
              }`}>
              View all
            </button>
          </div>
          <div className="space-y-2.5 sm:space-y-3 lg:space-y-4">
            {dashboardData.recentSales.length === 0 ? (
              <div className={`text-center py-8 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <ShoppingCart className={`w-12 h-12 mx-auto mb-3 ${
                  theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
                }`} />
                <p className="text-sm font-medium">No recent sales</p>
                <p className="text-xs mt-1">Sales transactions will appear here</p>
              </div>
            ) : (
              dashboardData.recentSales.map((sale, index) => (
                <React.Fragment key={sale.id}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        theme === 'dark' ? 'bg-slate-700' : 'bg-gray-100'
                      }`}>
                        <ShoppingCart size={14} className={`sm:w-4 sm:h-4 ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`font-medium text-sm sm:text-base truncate ${
                          theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {sale.customer_name}
                        </p>
                        <div className="flex items-center space-x-2 mt-0.5">
                          <p className={`text-xs sm:text-sm ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {new Date(sale.sale_date).toLocaleDateString()}
                          </p>
                          <span className={`px-2 py-0.5 text-xs rounded-full flex-shrink-0 ${
                            sale.payment_status === 'paid'
                              ? theme === 'dark' 
                                ? 'bg-green-900/50 text-green-400' 
                                : 'bg-green-100 text-green-700'
                              : sale.payment_status === 'partial'
                                ? theme === 'dark'
                                  ? 'bg-yellow-900/50 text-yellow-400'
                                  : 'bg-yellow-100 text-yellow-700'
                                : theme === 'dark'
                                  ? 'bg-red-900/50 text-red-400'
                                  : 'bg-red-100 text-red-700'
                          }`}>
                            {sale.payment_status.charAt(0).toUpperCase() + sale.payment_status.slice(1)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <div className={`text-sm sm:text-lg font-semibold ${
                        theme === 'dark' ? 'text-green-400' : 'text-green-600'
                      }`}>
                        {formatCurrency(sale.total_amount)}
                      </div>
                      <p className={`text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {sale.items_count} items
                      </p>
                    </div>
                  </div>
                  {index < dashboardData.recentSales.length - 1 && (
                    <div className={`border-t ${
                      theme === 'dark' ? 'border-slate-700' : 'border-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              ))
            )}
          </div>
        </div>

        {/* Low Stock Alert */}
        <div className={`p-3 sm:p-4 lg:p-6 rounded-2xl border ${
          theme === 'dark'
            ? 'bg-slate-800 border-slate-700'
            : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between mb-3 sm:mb-4 lg:mb-6">
            <h2 className={`text-lg sm:text-xl font-semibold ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Low Stock Alert
            </h2>
            <AlertTriangle className={`w-4 h-4 sm:w-5 sm:h-5 ${
              theme === 'dark' ? 'text-yellow-400' : 'text-yellow-500'
            }`} />
          </div>
          
          <div className="space-y-3 sm:space-y-4">
            {dashboardData.lowStockProducts.length === 0 ? (
              <div className={`text-center py-8 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <Package className={`w-12 h-12 mx-auto mb-3 ${
                  theme === 'dark' ? 'text-gray-600' : 'text-gray-300'
                }`} />
                <p className="text-sm font-medium">No low stock products</p>
                <p className="text-xs mt-1">All products are well stocked</p>
              </div>
            ) : (
              dashboardData.lowStockProducts.map((product) => (
                <div key={product.id} className={`p-3 sm:p-4 rounded-xl border ${
                theme === 'dark'
                  ? 'bg-slate-700/50 border-slate-600'
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className={`font-medium text-sm sm:text-base truncate ${
                      theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {product.name}
                    </p>
                    <p className={`text-xs sm:text-sm truncate ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {product.brand}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2">
                    <p className={`text-lg sm:text-xl font-bold ${
                      product.current_stock <= product.minimum_stock_level / 2
                        ? 'text-red-500'
                        : theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
                    }`}>
                      {product.current_stock}
                    </p>
                    <p className={`text-xs ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Min: {product.minimum_stock_level}
                    </p>
                  </div>
                </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;